<!--
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T22:30:00+05:00
Intent: Receipt submissions card: filter by review state and level, a row per receipt, and a review drawer with the image, what the reader found, each rule's result and approve / reject.
-->
<section class="luqta-card receipt-card" aria-labelledby="receipt-review-title">
  <header class="receipt-head">
    <div class="receipt-head__text">
      <h3 id="receipt-review-title">
        {{ 'receiptReview.title' | translate }}
        <span class="receipt-count" *ngIf="counts?.review" nz-tooltip
          [nzTooltipTitle]="'receiptReview.waitingHint' | translate">
          {{ counts?.review }} {{ 'receiptReview.waiting' | translate }}
        </span>
      </h3>
      <p class="receipt-sub">{{ 'receiptReview.subtitle' | translate }}</p>
    </div>
    <label class="receipt-level" *ngIf="levels.length > 1">
      <span class="visually-hidden">{{ 'receiptReview.level' | translate }}</span>
      <select [ngModel]="levelId" (ngModelChange)="setLevel($event)" [disabled]="!available">
        <option value="">{{ 'receiptReview.allLevels' | translate }}</option>
        <option *ngFor="let level of levels" [value]="level.id">{{ levelOptionName(level) }}</option>
      </select>
    </label>
  </header>

  <p class="receipt-note" *ngIf="!available && !loadFailed" role="note">
    <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
    {{ 'receiptReview.notConnected' | translate }}
  </p>

  <div class="receipt-filters" role="group" [attr.aria-label]="'receiptReview.filterLabel' | translate" *ngIf="available">
    <button type="button" *ngFor="let f of filters" class="receipt-filter" [class.is-active]="filter === f"
      [attr.aria-pressed]="filter === f" (click)="setFilter(f)" [disabled]="loading && filter !== f">
      {{ filterLabel(f) }}
      <span class="receipt-filter__count" *ngIf="countFor(f) !== null">{{ countFor(f) }}</span>
    </button>
  </div>

  <div class="receipt-error" *ngIf="loadFailed" role="alert">
    <span>{{ 'receiptReview.loadFailed' | translate }}</span>
    <button type="button" class="receipt-btn receipt-btn--ghost" (click)="load(page)">{{ 'receiptReview.retry' | translate }}</button>
  </div>

  <nz-table *ngIf="!loadFailed" #receiptTable [nzData]="submissions" [nzBordered]="true" [nzLoading]="loading"
    [nzFrontPagination]="false" [nzTotal]="total" [nzPageIndex]="page" [nzPageSize]="perPage"
    [nzShowPagination]="total > perPage" (nzPageIndexChange)="load($event)" [nzScroll]="{ x: '1100px' }"
    [nzNoResult]="emptyState">
    <thead>
      <tr>
        <th nzWidth="200px">{{ 'receiptReview.col.participant' | translate }}</th>
        <th nzWidth="160px">{{ 'receiptReview.col.level' | translate }}</th>
        <th nzWidth="190px">{{ 'receiptReview.col.shop' | translate }}</th>
        <th nzWidth="130px">{{ 'receiptReview.col.total' | translate }}</th>
        <th nzWidth="110px">{{ 'receiptReview.col.rules' | translate }}</th>
        <th nzWidth="130px">{{ 'receiptReview.col.status' | translate }}</th>
        <th nzWidth="130px">{{ 'receiptReview.col.submitted' | translate }}</th>
        <th nzWidth="110px" nzRight><span class="visually-hidden">{{ 'receiptReview.col.action' | translate }}</span></th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let submission of receiptTable.data; trackBy: trackSubmission">
        <td>
          <span class="receipt-strong">{{ submission.participant_name || ('receiptReview.unnamed' | translate) }}</span>
          <span class="receipt-muted receipt-ltr" dir="ltr" *ngIf="submission.phone_number">{{ submission.phone_number }}</span>
        </td>
        <td><span class="receipt-strong">{{ levelName(submission) }}</span></td>
        <td>
          <span dir="auto">{{ submission.shop || ('receiptReview.notRead' | translate) }}</span>
          <span class="receipt-muted" *ngIf="submission.receipt_date">{{ when(submission.receipt_date, false) }}</span>
        </td>
        <td class="receipt-num" dir="ltr">{{ money(submission.total, submission.currency) }}</td>
        <td>
          <ng-container *ngIf="tally(submission) as t">
            <span class="receipt-pill" *ngIf="t.total; else noRules"
              [ngClass]="t.passed === t.total ? 'is-success' : 'is-danger'">
              {{ 'receiptReview.rulesPassed' | translate: { passed: t.passed, total: t.total } }}
            </span>
          </ng-container>
          <ng-template #noRules><span class="receipt-muted">—</span></ng-template>
        </td>
        <td><span class="receipt-pill" [ngClass]="stateTone(submission)">{{ stateLabel(submission) }}</span></td>
        <td class="receipt-num">{{ when(submission.submitted_at) }}</td>
        <td nzRight>
          <button type="button" class="receipt-btn receipt-btn--sm"
            [class.receipt-btn--ghost]="!canDecide(submission)" (click)="open(submission)">
            {{ (canDecide(submission) ? 'receiptReview.review' : 'receiptReview.view') | translate }}
          </button>
        </td>
      </tr>
    </tbody>
  </nz-table>

  <ng-template #emptyState>
    <div class="receipt-empty">
      <i class="fa-regular fa-file-lines" aria-hidden="true"></i>
      <p>{{ (available ? (filter ? 'receiptReview.emptyFiltered' : 'receiptReview.empty') : 'receiptReview.emptyNotConnected') | translate }}</p>
    </div>
  </ng-template>
</section>

<nz-drawer [nzVisible]="!!selected" [nzClosable]="!deciding" [nzMaskClosable]="!deciding" [nzWidth]="drawerWidth"
  [nzPlacement]="isArabic ? 'left' : 'right'" [nzTitle]="drawerTitle" (nzOnClose)="close()">
  <ng-container *nzDrawerContent>
    <div class="receipt-drawer" *ngIf="selected as s">
      <div class="receipt-drawer__status">
        <span class="receipt-pill" [ngClass]="stateTone(s)">{{ stateLabel(s) }}</span>
        <span class="receipt-muted">{{ 'receiptReview.submitted' | translate }} {{ when(s.submitted_at) }}</span>
      </div>

      <figure class="receipt-image">
        <ng-container *ngIf="s.image_url && !imageFailed; else noImage">
          <img [src]="s.image_url" [alt]="'receiptReview.imageAlt' | translate" loading="lazy" (error)="imageFailed = true" />
          <a [href]="s.image_url" target="_blank" rel="noopener noreferrer" class="receipt-link">
            <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
            {{ 'receiptReview.openImage' | translate }}
          </a>
        </ng-container>
        <ng-template #noImage>
          <div class="receipt-image__empty">
            <i class="fa-regular fa-image" aria-hidden="true"></i>
            <span>{{ 'receiptReview.noImage' | translate }}</span>
          </div>
        </ng-template>
      </figure>

      <section class="receipt-block" aria-labelledby="receipt-read-title">
        <h4 id="receipt-read-title">{{ 'receiptReview.readValues' | translate }}</h4>
        <dl class="receipt-facts">
          <dt>{{ 'receiptReview.shop' | translate }}</dt>
          <dd dir="auto">{{ s.shop || ('receiptReview.notRead' | translate) }}</dd>
          <dt>{{ 'receiptReview.date' | translate }}</dt>
          <dd>{{ s.receipt_date ? when(s.receipt_date, false) : ('receiptReview.notRead' | translate) }}</dd>
          <dt>{{ 'receiptReview.total' | translate }}</dt>
          <dd dir="ltr" class="receipt-num receipt-facts__ltr">{{ money(s.total, s.currency) }}</dd>
          <dt>{{ 'receiptReview.confidence' | translate }}</dt>
          <dd class="receipt-num">{{ confidenceText(s.confidence) }}</dd>
        </dl>

        <h5>{{ 'receiptReview.products' | translate }}</h5>
        <ul class="receipt-items" *ngIf="s.items.length; else noItems">
          <li *ngFor="let item of s.items">
            <div class="receipt-items__main">
              <span dir="auto">{{ item.name }}</span>
              <span class="receipt-match" *ngIf="item.matched_product">
                <i class="fa-solid fa-check" aria-hidden="true"></i>
                {{ 'receiptReview.matched' | translate: { product: item.matched_product } }}
              </span>
            </div>
            <span class="receipt-num receipt-muted" dir="ltr">
              <ng-container *ngIf="item.quantity !== null">× {{ item.quantity }}</ng-container>
              <ng-container *ngIf="item.price !== null"> · {{ money(item.price, s.currency) }}</ng-container>
            </span>
          </li>
        </ul>
        <ng-template #noItems><p class="receipt-muted">{{ 'receiptReview.noItems' | translate }}</p></ng-template>
      </section>

      <section class="receipt-block" aria-labelledby="receipt-rules-title">
        <h4 id="receipt-rules-title">{{ 'receiptReview.ruleResults' | translate }}</h4>
        <ul class="receipt-rules" *ngIf="s.rules.length; else noRuleResults">
          <li *ngFor="let rule of s.rules" [ngClass]="ruleTone(rule)">
            <i class="fa-solid" [ngClass]="ruleIcon(rule)" aria-hidden="true"></i>
            <div>
              <span class="receipt-strong">{{ ruleLabel(rule) }}</span>
              <span class="visually-hidden">
                {{ (rule.passed === true ? 'receiptReview.passed' : rule.passed === false ? 'receiptReview.failed' : 'receiptReview.notChecked') | translate }}
              </span>
              <span class="receipt-muted" *ngIf="rule.expected || rule.actual" dir="auto">
                <ng-container *ngIf="rule.expected">{{ 'receiptReview.expected' | translate }}: {{ rule.expected }}</ng-container>
                <ng-container *ngIf="rule.expected && rule.actual"> · </ng-container>
                <ng-container *ngIf="rule.actual">{{ 'receiptReview.found' | translate }}: {{ rule.actual }}</ng-container>
              </span>
              <span class="receipt-muted" *ngIf="rule.detail" dir="auto">{{ rule.detail }}</span>
            </div>
          </li>
        </ul>
        <ng-template #noRuleResults><p class="receipt-muted">{{ 'receiptReview.noRules' | translate }}</p></ng-template>
      </section>

      <section class="receipt-block" *ngIf="s.reason || s.reviewed_by || s.reviewed_at">
        <h4>{{ 'receiptReview.decision' | translate }}</h4>
        <dl class="receipt-facts">
          <ng-container *ngIf="s.reason">
            <dt>{{ 'receiptReview.reason' | translate }}</dt>
            <dd dir="auto">{{ s.reason }}</dd>
          </ng-container>
          <ng-container *ngIf="s.reviewed_by">
            <dt>{{ 'receiptReview.reviewedBy' | translate }}</dt>
            <dd dir="auto">{{ s.reviewed_by }}</dd>
          </ng-container>
          <ng-container *ngIf="s.reviewed_at">
            <dt>{{ 'receiptReview.reviewedAt' | translate }}</dt>
            <dd class="receipt-num">{{ when(s.reviewed_at) }}</dd>
          </ng-container>
        </dl>
      </section>

      <footer class="receipt-actions" *ngIf="canDecide(s)">
        <ng-container *ngIf="!rejecting; else rejectForm">
          <button type="button" class="receipt-btn receipt-btn--danger-ghost" (click)="startReject()" [disabled]="!!deciding">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>{{ 'receiptReview.reject' | translate }}
          </button>
          <button type="button" class="receipt-btn" (click)="decide('approve')" [disabled]="!!deciding"
            [attr.aria-busy]="deciding === 'approve'">
            <i class="fa-solid" [ngClass]="deciding === 'approve' ? 'fa-spinner fa-spin' : 'fa-check'" aria-hidden="true"></i>
            {{ 'receiptReview.approve' | translate }}
          </button>
        </ng-container>
        <ng-template #rejectForm>
          <div class="receipt-reject">
            <label for="receipt-reject-reason">{{ 'receiptReview.rejectReason' | translate }}</label>
            <textarea id="receipt-reject-reason" rows="3" maxlength="300" [(ngModel)]="rejectReason"
              [placeholder]="'receiptReview.rejectPlaceholder' | translate" aria-describedby="receipt-reject-help"></textarea>
            <p id="receipt-reject-help" class="receipt-muted">{{ 'receiptReview.rejectHelp' | translate }}</p>
            <div class="receipt-reject__buttons">
              <button type="button" class="receipt-btn receipt-btn--ghost" (click)="cancelReject()" [disabled]="!!deciding">
                {{ 'receiptReview.cancel' | translate }}
              </button>
              <button type="button" class="receipt-btn receipt-btn--danger" (click)="decide('reject')"
                [disabled]="!!deciding || !rejectReason.trim()" [attr.aria-busy]="deciding === 'reject'">
                <i class="fa-solid" [ngClass]="deciding === 'reject' ? 'fa-spinner fa-spin' : 'fa-xmark'" aria-hidden="true"></i>
                {{ 'receiptReview.confirmReject' | translate }}
              </button>
            </div>
          </div>
        </ng-template>
      </footer>
    </div>
  </ng-container>
</nz-drawer>

<ng-template #drawerTitle>
  <span class="receipt-drawer__title" *ngIf="selected">
    {{ selected.participant_name || ('receiptReview.unnamed' | translate) }}
    <span class="receipt-muted">{{ levelName(selected) }}</span>
  </span>
</ng-template>
